import {
  ProviderSettingsRequestError,
  requestProviderSettings,
} from '@/services/provider/settings-request';
import type {
  CustomerPortalMenuSettings,
  UpdateCustomerPortalMenuInput,
} from '@/types/customer-portal-menu';
import { normalizePortalMenuItems } from '@/lib/portal-menu';

type ApiPayload = {
  data?: unknown;
  message?: string;
  errors?: Record<string, string[]>;
};

function normalizeSettings(payload: ApiPayload | undefined): CustomerPortalMenuSettings {
  const record =
    (payload?.data as Record<string, unknown> | undefined) ??
    ((payload as Record<string, unknown> | undefined) ?? {});

  return {
    items: normalizePortalMenuItems(record.items),
  };
}

export class ProviderCustomerPortalMenuRequestError extends ProviderSettingsRequestError {
  errors?: Record<string, string[]>;

  constructor(message: string, status?: number, payload?: unknown) {
    super(message, status, payload);
    if (payload && typeof payload === 'object' && 'errors' in (payload as Record<string, unknown>)) {
      this.errors = (payload as { errors?: Record<string, string[]> }).errors;
    }
  }
}

async function requestProviderCustomerPortalMenu(
  path: string,
  options: { method?: 'GET' | 'PUT'; body?: Record<string, unknown> | undefined } = {},
): Promise<ApiPayload> {
  try {
    return (await requestProviderSettings(path, options)) as ApiPayload;
  } catch (error) {
    if (error instanceof ProviderSettingsRequestError) {
      throw new ProviderCustomerPortalMenuRequestError(error.message, error.status, error.payload);
    }
    throw new ProviderCustomerPortalMenuRequestError(
      'Provider customer portal menu request failed',
      undefined,
      error,
    );
  }
}

class ProviderCustomerPortalMenuService {
  async getSettings(): Promise<CustomerPortalMenuSettings> {
    const response = await requestProviderCustomerPortalMenu(
      '/api/v1/provider/settings/customer-portal-menu',
    );
    return normalizeSettings(response);
  }

  async updateSettings(
    input: UpdateCustomerPortalMenuInput,
  ): Promise<CustomerPortalMenuSettings> {
    const response = await requestProviderCustomerPortalMenu(
      '/api/v1/provider/settings/customer-portal-menu',
      {
        method: 'PUT',
        body: { items: input.items },
      },
    );
    return normalizeSettings(response);
  }
}

export const providerCustomerPortalMenuService = new ProviderCustomerPortalMenuService();
